import { bindPillsOverflow } from './carousel';

function resetCap(form) {
    const widget = form.querySelector('cap-widget');
    if (widget && typeof widget.reset === 'function') {
        widget.reset();
    }
}

function newsletterValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export default function newsletter() {
    const modal = document.getElementById('taohs-newsletter');
    if (!modal) {
        return;
    }

    const form = modal.querySelector('.js-newsletter-form');
    const status = modal.querySelector('.js-newsletter-status');
    const submit = form?.querySelector('button[type="submit"]');
    const emailInput = form?.querySelector('input[name="email"]');

    const setStatus = (message, isError) => {
        if (!status) {
            return;
        }
        status.textContent = message;
        status.classList.toggle('is-error', Boolean(isError));
    };

    const open = (event) => {
        if (event) {
            event.preventDefault();
        }
        modal.hidden = false;
        modal.classList.add('is-open');
    };

    const close = () => {
        modal.hidden = true;
        modal.classList.remove('is-open');
        setStatus('');
    };

    document.querySelectorAll('.js-newsletter').forEach((el) => {
        el.addEventListener('click', open);
    });
    modal.querySelector('.js-newsletter-close')?.addEventListener('click', close);
    modal.addEventListener('click', (event) => {
        if (event.target === modal) {
            close();
        }
    });

    form?.addEventListener('submit', async (event) => {
        event.preventDefault();
        const endpoint = (form.getAttribute('data-endpoint') || '').trim();
        const email = String(emailInput?.value || '').trim();

        if (!newsletterValidEmail(email)) {
            setStatus('Enter a valid email address.', true);
            return;
        }
        const capToken = String(new FormData(form).get('cap-token') || '').trim();
        if (!capToken) {
            setStatus('Verify you are human before sending.', true);
            return;
        }
        if (!endpoint) {
            setStatus('This form is not connected yet.', true);
            return;
        }

        if (submit) {
            submit.disabled = true;
        }
        setStatus('Subscribing…');

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json'
                },
                body: JSON.stringify({ email, cap_token: capToken })
            });
            let payload = {};
            try {
                payload = await response.json();
            } catch {
                payload = {};
            }
            if (!response.ok) {
                setStatus(payload.error || 'Could not subscribe. Try again later.', true);
                return;
            }
            if (emailInput) {
                emailInput.value = '';
            }
            resetCap(form);
            setStatus("You're subscribed. Thank you.");
            window.setTimeout(close, 2000);
        } catch {
            setStatus('Could not subscribe. Try again later.', true);
        } finally {
            if (submit) {
                submit.disabled = false;
            }
        }
    });
}

export function unsubscribeForm() {
    const form = document.querySelector('.js-unsubscribe-form');
    if (!form) {
        return;
    }

    const status = form.querySelector('.js-unsubscribe-status');
    const submit = form.querySelector('button[type="submit"]');
    const emailInput = form.querySelector('input[name="email"]');

    const setStatus = (message, isError) => {
        if (!status) {
            return;
        }
        status.textContent = message;
        status.classList.toggle('is-error', Boolean(isError));
    };

    const params = new URLSearchParams(window.location.search);
    const prefilledEmail = String(params.get('email') || '').trim();
    if (prefilledEmail && emailInput) {
        emailInput.value = prefilledEmail;
    }

    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const endpoint = (form.getAttribute('data-endpoint') || '').trim();
        const email = String(emailInput?.value || '').trim();

        if (!newsletterValidEmail(email)) {
            setStatus('Enter a valid email address.', true);
            return;
        }
        const capToken = String(new FormData(form).get('cap-token') || '').trim();
        if (!capToken) {
            setStatus('Verify you are human before sending.', true);
            return;
        }
        if (!endpoint) {
            setStatus('This form is not connected yet.', true);
            return;
        }

        if (submit) {
            submit.disabled = true;
        }
        setStatus('Unsubscribing…');

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json'
                },
                body: JSON.stringify({ email, cap_token: capToken })
            });
            let payload = {};
            try {
                payload = await response.json();
            } catch {
                payload = {};
            }
            if (!response.ok) {
                setStatus(payload.error || 'Could not unsubscribe. Try again later.', true);
                return;
            }
            if (emailInput) {
                emailInput.value = '';
            }
            resetCap(form);
            setStatus("You're unsubscribed.");
        } catch {
            setStatus('Could not unsubscribe. Try again later.', true);
        } finally {
            if (submit) {
                submit.disabled = false;
            }
        }
    });
}

export function share() {
    document.querySelectorAll('.js-share').forEach((button) => {
        const label = button.querySelector('.js-share-label');
        const original = label?.textContent?.trim() || 'Share this story';
        button.addEventListener('click', async () => {
            const url = button.getAttribute('data-url') || window.location.href;
            try {
                await navigator.clipboard.writeText(url);
                if (label) {
                    label.textContent = 'Copied';
                    window.setTimeout(() => {
                        label.textContent = original;
                    }, 2000);
                } else {
                    button.textContent = 'Copied';
                }
            } catch (error) {
                window.prompt('Copy this link', url);
            }
        });
    });
}

const CONTACT_INTERESTS = [
    'Commissioned Storytelling',
    'Franchise Partnership',
    'Join the Creative Network',
    'Other'
];

const CONTACT_PHONE = {
    NG: { prefix: '234', length: 10, placeholder: '+234 800 000 0000' },
    US: { prefix: '1', length: 10, placeholder: '+1 (555) 000-0000' },
    GB: { prefix: '44', length: 10, placeholder: '+44 7700 000000' }
};

function contactDigits(value) {
    return String(value || '').replace(/\D/g, '');
}

function contactNormalizePhone(country, raw) {
    const rule = CONTACT_PHONE[country] || CONTACT_PHONE.NG;
    let digits = contactDigits(raw);
    if (digits.startsWith(rule.prefix)) {
        digits = digits.slice(rule.prefix.length);
    }
    if (country === 'NG' && digits.startsWith('0')) {
        digits = digits.slice(1);
    }
    return `+${rule.prefix}${digits}`;
}

function contactValidPhone(country, raw) {
    const rule = CONTACT_PHONE[country];
    if (!rule || !String(raw || '').trim()) {
        return false;
    }
    const normalized = contactNormalizePhone(country, raw);
    return new RegExp(`^\\+${rule.prefix}\\d{${rule.length}}$`).test(normalized);
}

function contactValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

export function contactForm() {
    const form = document.querySelector('.js-contact-form');
    if (!form) {
        return;
    }

    const status = form.querySelector('.js-contact-status');
    const submit = form.querySelector('button[type="submit"]');
    const country = form.querySelector('#phone-country');
    const phone = form.querySelector('#phone');
    const interest = form.querySelector('#interest');

    const setStatus = (message, isError) => {
        if (!status) {
            return;
        }
        status.textContent = message;
        status.classList.toggle('is-error', Boolean(isError));
    };

    const applyPhonePlaceholder = () => {
        const rule = CONTACT_PHONE[country?.value] || CONTACT_PHONE.NG;
        if (phone) {
            phone.placeholder = rule.placeholder;
        }
    };

    const applyInterest = (value) => {
        if (!interest || !CONTACT_INTERESTS.includes(value)) {
            return;
        }
        interest.value = value;
    };

    country?.addEventListener('change', applyPhonePlaceholder);
    applyPhonePlaceholder();

    document.querySelectorAll('[data-interest]').forEach((link) => {
        link.addEventListener('click', (event) => {
            applyInterest(link.getAttribute('data-interest') || '');
            if (window.matchMedia('(min-width: 901px)').matches) {
                event.preventDefault();
                form.classList.remove('is-highlight');
                void form.offsetWidth;
                form.classList.add('is-highlight');
                form.querySelector('#first-name')?.focus();
                window.setTimeout(() => {
                    form.classList.remove('is-highlight');
                }, 1600);
            }
        });
    });

    if (window.location.hash === '#contact') {
        const params = new URLSearchParams(window.location.search);
        applyInterest(params.get('interest') || '');
    }

    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const endpoint = (form.getAttribute('data-endpoint') || '').trim();
        const payload = Object.fromEntries(new FormData(form).entries());
        payload.privacy = form.querySelector('[name="privacy"]')?.checked === true;
        payload.first_name = String(payload.first_name || '').trim();
        payload.last_name = String(payload.last_name || '').trim();
        payload.email = String(payload.email || '').trim();
        payload.message = String(payload.message || '').trim();
        payload.phone_country = CONTACT_PHONE[payload.phone_country] ? payload.phone_country : 'NG';
        payload.interest = String(payload.interest || '').trim();

        if (!payload.first_name || !payload.last_name) {
            setStatus('Enter your first and last name.', true);
            return;
        }
        if (!contactValidEmail(payload.email)) {
            setStatus('Enter a valid email address.', true);
            return;
        }
        if (!contactValidPhone(payload.phone_country, payload.phone)) {
            setStatus('Enter a valid phone number for the selected country (10 digits).', true);
            return;
        }
        if (!CONTACT_INTERESTS.includes(payload.interest)) {
            setStatus('Select what you are interested in.', true);
            return;
        }
        if (!payload.message) {
            setStatus('Enter a message.', true);
            return;
        }
        if (!payload.privacy) {
            setStatus('Please agree to the privacy policy before sending.', true);
            return;
        }

        payload.phone = contactNormalizePhone(payload.phone_country, payload.phone);
        payload.cap_token = String(payload['cap-token'] || '').trim();
        delete payload['cap-token'];
        if (!payload.cap_token) {
            setStatus('Verify you are human before sending.', true);
            return;
        }

        if (!endpoint) {
            setStatus('This form is ready. An API endpoint will be connected here later.');
            return;
        }

        if (submit) {
            submit.disabled = true;
        }
        setStatus('Sending…');

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json'
                },
                body: JSON.stringify(payload)
            });
            if (!response.ok) {
                throw new Error('Request failed');
            }
            form.reset();
            resetCap(form);
            if (country) {
                country.value = 'NG';
            }
            applyPhonePlaceholder();
            if (interest) {
                interest.selectedIndex = 0;
            }
            setStatus('Message sent. Thank you.');
        } catch (error) {
            setStatus('Could not send the message. Try again later.', true);
        } finally {
            if (submit) {
                submit.disabled = false;
            }
        }
    });
}

const INDEX_FRANCHISES = [
    'category_the-room',
    'category_elsewhere',
    'category_archive-of-us'
];
const EDITORIAL_CATS = [
    'visionary-leadership',
    'travel',
    'hospitality-social-life',
    'creative-communities',
    'art-design',
    'fashion-style',
    'culture',
    'film-documentary'
];
const FRANCHISE_LABELS = {
    'category_the-room': 'The Room',
    'category_elsewhere': 'Elsewhere',
    'category_archive-of-us': 'Archive of Us',
    'category_taohs-in-motion': 'TAOHS in Motion'
};
const SEARCH_MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

function indexHasFranchise(tags, prefix) {
    return tags.some((slug) => slug === prefix || slug.startsWith(`${prefix}_`));
}

export function leafLabel(value) {
    const parts = String(value || '').split(' / ');
    return (parts[parts.length - 1] || value || '').trim();
}

function tagHasEditorialLeaf(tags, leaf) {
    return tags.some((slug) => slug === leaf || slug.endsWith(`_${leaf}`));
}

function hideEmptyEditorialPills(items, pills) {
    const present = new Set();
    items.forEach((item) => {
        const tags = (item.getAttribute('data-tags') || '').trim().split(/\s+/).filter(Boolean);
        EDITORIAL_CATS.forEach((leaf) => {
            if (tagHasEditorialLeaf(tags, leaf)) {
                present.add(leaf);
            }
        });
    });
    pills.forEach((link) => {
        const cat = link.getAttribute('data-cat') || '';
        if (!cat) {
            return;
        }
        link.hidden = !present.has(cat);
    });
    bindPillsOverflow();
}

export function applyLeafLabels() {
    document.querySelectorAll('.taohs-cat, .taohs-search-card-cat').forEach((el) => {
        if (el.dataset.leafApplied) {
            return;
        }
        el.dataset.leafApplied = '1';
        el.textContent = leafLabel(el.textContent);
        if (!el.textContent) {
            el.remove();
        }
    });
    document.querySelectorAll('.js-leaf-heading').forEach((el) => {
        const raw = el.textContent.replace(/^More from\s+/i, '');
        el.textContent = `More from ${leafLabel(raw)}`;
    });
    const leafHrefs = new Set(
        [...document.querySelectorAll('[data-more-leaf] a[href]')].map((link) => link.getAttribute('href'))
    );
    document.querySelectorAll('.js-franchise-heading').forEach((el) => {
        const tags = (el.getAttribute('data-tags') || '').split(/\s+/).filter(Boolean);
        const prefix = INDEX_FRANCHISES.find((key) => tags.some((slug) => slug === key || slug.startsWith(`${key}_`)));
        if (prefix) {
            el.textContent = `More from ${FRANCHISE_LABELS[prefix]}`;
        }
        const pool = el.parentElement?.querySelector('.js-franchise-more-pool');
        if (!pool) {
            return;
        }
        const column = el.parentElement;
        const cards = [...pool.querySelectorAll('.js-more-candidate')];
        let kept = false;
        cards.forEach((card) => {
            const cardTags = (card.getAttribute('data-tags') || '').split(/\s+/).filter(Boolean);
            const href = card.querySelector('a[href]')?.getAttribute('href');
            const match = prefix && cardTags.some((slug) => slug === prefix || slug.startsWith(`${prefix}_`));
            if (match && !kept && !leafHrefs.has(href)) {
                kept = true;
                return;
            }
            card.remove();
        });
        if (!kept) {
            column?.remove();
        }
    });
    document.querySelectorAll('.taohs-more').forEach((aside) => {
        if (!aside.querySelector('.taohs-more-col')) {
            aside.hidden = true;
        }
    });
}

const DATE_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDateRangeLabel(start, end) {
    if (!start) {
        return 'Dates';
    }
    const fmt = (key) => {
        const [year, month, day] = String(key).split('-').map(Number);
        if (!year || !month || !day) {
            return key;
        }
        return `${day} ${DATE_SHORT[month - 1]}`;
    };
    if (!end || end === start) {
        return fmt(start);
    }
    return `${fmt(start)} – ${fmt(end)}`;
}

function searchDateKey(year, month, day) {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function inDateRange(value, start, end) {
    if (!start) {
        return true;
    }
    const day = String(value || '').slice(0, 10);
    if (!day) {
        return false;
    }
    if (day < start) {
        return false;
    }
    return day <= (end || start);
}

function bindDropMenus(root) {
    const drops = [...root.querySelectorAll('.js-search-drop')];
    const closeDrops = (keep) => {
        drops.forEach((drop) => {
            if (drop === keep) {
                return;
            }
            drop.classList.remove('is-open');
            drop.querySelectorAll('[aria-expanded]').forEach((btn) => {
                btn.setAttribute('aria-expanded', 'false');
            });
            drop.querySelectorAll('.taohs-search-menu, .js-search-dates, .js-archive-dates').forEach((menu) => {
                menu.hidden = true;
            });
        });
    };
    drops.forEach((drop) => {
        const button = drop.querySelector('.taohs-search-drop-btn');
        const menu = drop.querySelector('.taohs-search-menu, .js-search-dates, .js-archive-dates');
        button?.addEventListener('click', (event) => {
            event.stopPropagation();
            const open = drop.classList.contains('is-open');
            closeDrops();
            if (!open && menu) {
                drop.classList.add('is-open');
                button.setAttribute('aria-expanded', 'true');
                menu.hidden = false;
            }
        });
        menu?.addEventListener('click', (event) => {
            event.stopPropagation();
        });
    });
    document.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Node) || !target.isConnected) {
            return;
        }
        if (!root.contains(target) || !target.closest('.js-search-drop')) {
            closeDrops();
        }
    });
    return { drops, closeDrops };
}

function bindFilterDrawer(root) {
    const drawer = root.querySelector('.js-archive-drawer');
    if (!drawer) {
        return;
    }
    root.querySelector('.js-archive-drawer-open')?.addEventListener('click', () => {
        drawer.classList.add('is-open');
    });
    root.querySelectorAll('.js-archive-drawer-close').forEach((btn) => {
        btn.addEventListener('click', () => {
            drawer.classList.remove('is-open');
        });
    });
}

function renderMonthGrid(grid, label, cal, onPick) {
    if (!grid || !label) {
        return;
    }
    const year = cal.view.getFullYear();
    const month = cal.view.getMonth();
    label.textContent = `${SEARCH_MONTHS[month]} ${year}`;
    const first = new Date(year, month, 1).getDay();
    const days = new Date(year, month + 1, 0).getDate();
    const nodes = [];
    for (let i = 0; i < first; i += 1) {
        nodes.push(document.createElement('span'));
    }
    for (let day = 1; day <= days; day += 1) {
        const key = searchDateKey(year, month, day);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = String(day);
        btn.setAttribute('data-date', key);
        if (cal.start && key === cal.start) {
            btn.classList.add('is-start', 'is-selected');
        }
        if (cal.end && key === cal.end) {
            btn.classList.add('is-end', 'is-selected');
        }
        if (cal.start && cal.end && key > cal.start && key < cal.end) {
            btn.classList.add('is-in-range');
        }
        btn.addEventListener('click', (event) => {
            event.stopPropagation();
            if (!cal.start || (cal.start && cal.end)) {
                cal.start = key;
                cal.end = null;
            } else if (key < cal.start) {
                cal.end = cal.start;
                cal.start = key;
            } else {
                cal.end = key;
            }
            onPick();
        });
        nodes.push(btn);
    }
    grid.replaceChildren(...nodes);
}

export function archiveFilters() {
    const items = [...document.querySelectorAll('.js-archive-item')];
    if (!items.length) {
        return;
    }
    const toolbar = document.querySelector('.taohs-archive-toolbar');
    const franchise = document.getElementById('taohs-filter-franchise');
    const category = document.getElementById('taohs-filter-category');
    const search = document.getElementById('taohs-index-search');
    const count = document.querySelector('.js-archive-count');
    const sortLabel = document.querySelector('.js-archive-sort-label');
    const filterLabel = document.querySelector('.js-archive-filter-label');
    const dateLabel = document.querySelector('.js-archive-date-label');
    const dateGrid = document.querySelector('.js-archive-date-grid');
    const dateMonth = document.querySelector('.js-archive-date-month');
    const clearBtn = document.querySelector('.js-archive-clear');
    const sortState = { value: 'newest' };
    const cal = { view: new Date(), start: null, end: null };

    const catPresent = new Set();
    const franchisePresent = new Set();
    items.forEach((item) => {
        const tags = (item.getAttribute('data-tags') || '').split(/\s+/).filter(Boolean);
        EDITORIAL_CATS.forEach((leaf) => {
            if (INDEX_FRANCHISES.some((prefix) => tags.includes(`${prefix}_${leaf}`))) {
                catPresent.add(leaf);
            }
        });
        INDEX_FRANCHISES.forEach((prefix) => {
            if (indexHasFranchise(tags, prefix)) {
                franchisePresent.add(prefix);
            }
        });
    });
    [...(category?.options || [])].forEach((opt) => {
        if (opt.value && !catPresent.has(opt.value)) {
            opt.remove();
        }
    });
    [...(franchise?.options || [])].forEach((opt) => {
        if (opt.value && !franchisePresent.has(opt.value)) {
            opt.remove();
        }
    });

    const closeDrops = toolbar ? bindDropMenus(toolbar).closeDrops : () => {};

    const paintCalendar = () => renderMonthGrid(dateGrid, dateMonth, cal, paintCalendar);

    const updateFilterLabel = () => {
        if (!filterLabel) {
            return;
        }
        const parts = [];
        if (franchise?.value) {
            parts.push(franchise.options[franchise.selectedIndex]?.textContent.trim() || '');
        }
        if (category?.value) {
            parts.push(category.options[category.selectedIndex]?.textContent.trim() || '');
        }
        filterLabel.textContent = parts.filter(Boolean).join(' + ') || 'Filters';
    };

    const apply = () => {
        const franchiseVal = franchise?.value || '';
        const categoryVal = category?.value || '';
        const searchVal = (search?.value || '').trim().toLowerCase();
        let visible = 0;
        items.forEach((item) => {
            const tags = (item.getAttribute('data-tags') || '').split(/\s+/).filter(Boolean);
            const day = item.getAttribute('data-date') || '';
            const title = (item.getAttribute('data-title') || '').toLowerCase();
            const excerpt = (item.getAttribute('data-excerpt') || '').toLowerCase();
            const okFranchise = franchiseVal
                ? indexHasFranchise(tags, franchiseVal)
                : INDEX_FRANCHISES.some((prefix) => indexHasFranchise(tags, prefix));
            const okCategory =
                !categoryVal ||
                INDEX_FRANCHISES.some((prefix) => tags.includes(`${prefix}_${categoryVal}`));
            const okDate = inDateRange(day, cal.start, cal.end);
            const okSearch =
                !searchVal ||
                title.includes(searchVal) ||
                excerpt.includes(searchVal) ||
                tags.join(' ').toLowerCase().includes(searchVal);
            const show = okFranchise && okCategory && okDate && okSearch;
            item.hidden = !show;
            if (show) {
                visible += 1;
            }
        });
        if (count) {
            count.textContent = String(visible);
        }
        items.sort((a, b) => {
            const left = a.getAttribute('data-date') || '';
            const right = b.getAttribute('data-date') || '';
            return sortState.value === 'oldest' ? left.localeCompare(right) : right.localeCompare(left);
        });
        const grid = document.querySelector('.js-archive-grid');
        items.forEach((item) => {
            if (grid && item.parentElement === grid) {
                grid.appendChild(item);
            }
        });
        if (clearBtn) {
            clearBtn.hidden = !(
                cal.start ||
                (franchise && franchise.value) ||
                (category && category.value) ||
                (search && search.value.trim()) ||
                sortState.value !== 'newest'
            );
        }
        updateFilterLabel();
    };

    document.querySelectorAll('[data-archive-sort]').forEach((button) => {
        button.addEventListener('click', () => {
            sortState.value = button.getAttribute('data-archive-sort') || 'newest';
            if (sortLabel) {
                sortLabel.textContent = sortState.value === 'oldest' ? 'Oldest' : 'Newest';
            }
            closeDrops();
            apply();
        });
    });
    franchise?.addEventListener('change', apply);
    category?.addEventListener('change', apply);
    search?.addEventListener('input', apply);
    document.querySelector('.js-archive-date-prev')?.addEventListener('click', () => {
        cal.view = new Date(cal.view.getFullYear(), cal.view.getMonth() - 1, 1);
        paintCalendar();
    });
    document.querySelector('.js-archive-date-next')?.addEventListener('click', () => {
        cal.view = new Date(cal.view.getFullYear(), cal.view.getMonth() + 1, 1);
        paintCalendar();
    });
    document.querySelector('.js-archive-date-cancel')?.addEventListener('click', () => {
        cal.start = null;
        cal.end = null;
        if (dateLabel) {
            dateLabel.textContent = 'Dates';
        }
        paintCalendar();
        closeDrops();
        apply();
    });
    document.querySelector('.js-archive-date-apply')?.addEventListener('click', () => {
        if (dateLabel) {
            dateLabel.textContent = formatDateRangeLabel(cal.start, cal.end);
        }
        closeDrops();
        apply();
        drawer?.classList.remove('is-open');
    });
    clearBtn?.addEventListener('click', () => {
        cal.start = null;
        cal.end = null;
        sortState.value = 'newest';
        if (franchise) {
            franchise.value = '';
        }
        if (category) {
            category.value = '';
        }
        if (search) {
            search.value = '';
        }
        if (sortLabel) {
            sortLabel.textContent = 'Newest';
        }
        if (dateLabel) {
            dateLabel.textContent = 'Dates';
        }
        paintCalendar();
        closeDrops();
        apply();
    });
    if (toolbar) {
        bindFilterDrawer(toolbar);
    }
    paintCalendar();
    apply();
}

const FRANCHISE_CATS = EDITORIAL_CATS;

export function franchiseViews() {
    const root = document.querySelector('.taohs-franchise');
    if (!root) {
        return;
    }

    const home = root.querySelector('[data-panel="home"]');
    const all = root.querySelector('[data-panel="all"]');
    const lists = [...root.querySelectorAll('[data-panel="list"]')];
    const pills = [...root.querySelectorAll('.taohs-pills a')];

    const apply = () => {
        const hash = (window.location.hash || '').replace('#', '');
        const isAll = hash === 'view-all';
        const isCat = FRANCHISE_CATS.includes(hash);

        if (home) {
            home.hidden = isAll || isCat;
        }
        if (all) {
            all.hidden = !isAll;
        }
        lists.forEach((el) => {
            el.hidden = el.getAttribute('data-cat') !== hash;
        });
        pills.forEach((link) => {
            const active =
                (isAll && link.getAttribute('data-view') === 'all') ||
                (isCat && link.getAttribute('data-cat') === hash);
            link.classList.toggle('is-active', active);
        });
    };

    window.addEventListener('hashchange', apply);
    apply();
}

const SEARCH_CATS = EDITORIAL_CATS;
const SEARCH_RESULT_CAP = 13;
const SEARCH_LISTING = ['category_the-room_', 'category_elsewhere_', 'category_archive-of-us_', 'category_taohs-in-motion_'];
const SEARCH_SORT_LABELS = {
    relevance: 'Relevance',
    newest: 'Latest - Oldest',
    oldest: 'Oldest - Latest'
};

function searchEscape(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function searchEscapeHtml(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function searchHighlight(root, query) {
    root.querySelectorAll('.js-search-text').forEach((el) => {
        if (!el.dataset.original) {
            el.dataset.original = el.textContent;
        }
        const original = el.dataset.original;
        if (!query) {
            el.textContent = original;
            return;
        }
        const re = new RegExp(`(${searchEscape(query)})`, 'ig');
        el.innerHTML = searchEscapeHtml(original).replace(re, '<mark>$1</mark>');
    });
}

function searchIsVideo(tags) {
    return tags.some((slug) => slug === 'category_taohs-in-motion' || slug.startsWith('category_taohs-in-motion_'));
}

function searchMatchesCat(tags, cat) {
    if (!cat) {
        return true;
    }
    return SEARCH_LISTING.some((prefix) => tags.includes(`${prefix}${cat}`))
        || tags.some((slug) => slug.endsWith(`_${cat}`));
}

function searchScore(item, query) {
    if (!query) {
        return 0;
    }
    const q = query.toLowerCase();
    const title = (item.getAttribute('data-title') || '').toLowerCase();
    const excerpt = (item.getAttribute('data-excerpt') || '').toLowerCase();
    const tags = (item.getAttribute('data-tag-names') || '').toLowerCase();
    if (title.startsWith(q)) {
        return 3;
    }
    if (title.includes(q)) {
        return 2;
    }
    if (excerpt.includes(q) || tags.includes(q)) {
        return 1;
    }
    return 0;
}

function searchClone(item, query) {
    const node = item.cloneNode(true);
    node.classList.remove('js-search-item');
    node.removeAttribute('hidden');
    searchHighlight(node, query);
    return node;
}

function searchFill(container, items, query) {
    container.replaceChildren(...items.map((item) => searchClone(item, query)));
}

function searchFillRow(container, items, query) {
    searchFill(container, items, query);
    container.classList.toggle('is-center', items.length === 3);
    container.hidden = items.length === 0;
}

export function searchPage() {
    const root = document.querySelector('[data-search]');
    if (!root) {
        return;
    }

    const items = [...root.querySelectorAll('.js-search-item')];
    const source = root.querySelector('.js-search-source');
    const heading = root.querySelector('.js-search-heading');
    const form = root.querySelector('.taohs-search-form');
    const input = root.querySelector('#taohs-search-q');
    const catInput = root.querySelector('.js-search-cat-input');
    const pills = [...root.querySelectorAll('.taohs-pills a')];
    const suggestRow = root.querySelector('.js-suggest-row');
    const suggestStack = root.querySelector('.js-suggest-stack');
    const results = root.querySelector('.js-results');
    const emptyLine = root.querySelector('.js-empty-line');
    const sortLabel = root.querySelector('.js-sort-label');
    const filterLabel = root.querySelector('.js-filter-label');
    const dateBtnLabel = root.querySelector('.js-date-btn-label');
    const articleFilter = root.querySelector('.js-filter-article');
    const videoFilter = root.querySelector('.js-filter-video');
    const dateGrid = root.querySelector('.js-date-grid');
    const dateLabel = root.querySelector('.js-date-label');
    const clearBtn = root.querySelector('.js-clear-filters');
    const toolbar = root.querySelector('.taohs-search-toolbar');
    const { closeDrops } = bindDropMenus(root);
    if (toolbar) {
        bindFilterDrawer(toolbar);
    }

    const sortState = { value: 'relevance' };
    const cal = {
        view: new Date(),
        start: null,
        end: null
    };

    const renderCalendar = () => {
        renderMonthGrid(dateGrid, dateLabel, cal, renderCalendar);
    };

    const readState = () => {
        const params = new URLSearchParams(window.location.search);
        const cat = params.get('cat') || '';
        const sort = params.get('sort') || sortState.value || 'relevance';
        return {
            q: (params.get('q') || '').trim(),
            cat: SEARCH_CATS.includes(cat) ? cat : '',
            sort: sort === 'custom' ? 'relevance' : sort
        };
    };

    const writeState = (state, replace) => {
        const params = new URLSearchParams();
        if (state.q) {
            params.set('q', state.q);
        }
        if (state.cat) {
            params.set('cat', state.cat);
        }
        if (state.sort && state.sort !== 'relevance') {
            params.set('sort', state.sort);
        }
        const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
        const method = replace ? 'replaceState' : 'pushState';
        history[method](state, '', next);
    };

    const render = () => {
        const state = readState();
        sortState.value = state.sort;
        if (input && document.activeElement !== input) {
            input.value = state.q;
        }
        if (catInput) {
            catInput.value = state.cat;
        }
        if (sortLabel) {
            sortLabel.textContent = SEARCH_SORT_LABELS[state.sort] || SEARCH_SORT_LABELS.relevance;
        }
        if (filterLabel) {
            const articleOn = Boolean(articleFilter?.checked);
            const videoOn = Boolean(videoFilter?.checked);
            if (articleOn && videoOn) {
                filterLabel.textContent = 'Article + Video';
            } else if (articleOn) {
                filterLabel.textContent = 'Article';
            } else if (videoOn) {
                filterLabel.textContent = 'Video';
            } else {
                filterLabel.textContent = 'Filters';
            }
        }
        pills.forEach((link) => {
            const cat = link.getAttribute('data-cat') || '';
            link.classList.toggle('is-active', cat === state.cat);
        });

        if (clearBtn) {
            clearBtn.hidden = !(
                cal.start ||
                articleFilter?.checked ||
                videoFilter?.checked ||
                state.sort !== 'relevance' ||
                state.cat ||
                state.q
            );
        }

        const articleOn = Boolean(articleFilter?.checked);
        const videoOn = Boolean(videoFilter?.checked);

        const typed = items.filter((item) => {
            const tags = (item.getAttribute('data-tags') || '').trim().split(/\s+/).filter(Boolean);
            const video = searchIsVideo(tags);
            if (articleOn && !videoOn) {
                return !video;
            }
            if (videoOn && !articleOn) {
                return video;
            }
            return true;
        }).filter((item) => inDateRange(item.getAttribute('data-date') || '', cal.start, cal.end));

        const inCat = typed.filter((item) => {
            const tags = (item.getAttribute('data-tags') || '').trim().split(/\s+/).filter(Boolean);
            return searchMatchesCat(tags, state.cat);
        });

        const scored = inCat.map((item) => ({
            item,
            score: searchScore(item, state.q),
            date: item.getAttribute('data-date') || '',
            featured: item.getAttribute('data-featured') === '1'
        }));

        const matches = state.q
            ? scored.filter((entry) => entry.score > 0)
            : scored;

        const bySort = [...matches];
        if (state.sort === 'oldest') {
            bySort.sort((a, b) => a.date.localeCompare(b.date));
        } else if (state.sort === 'newest') {
            bySort.sort((a, b) => b.date.localeCompare(a.date));
        } else {
            bySort.sort((a, b) => {
                if (b.score !== a.score) {
                    return b.score - a.score;
                }
                return b.date.localeCompare(a.date);
            });
        }

        const recentFive = [...inCat]
            .sort((a, b) => (b.getAttribute('data-date') || '').localeCompare(a.getAttribute('data-date') || ''))
            .slice(0, 5);

        const hasQuery = Boolean(state.q);
        const hasHits = hasQuery && bySort.length > 0;

        if (heading) {
            heading.textContent = hasQuery && !hasHits
                ? 'No stories found for your search.'
                : 'Search TAOHS';
        }

        if (emptyLine) {
            if (!hasQuery) {
                emptyLine.hidden = true;
                emptyLine.textContent = '';
            } else if (!hasHits) {
                emptyLine.hidden = false;
                emptyLine.innerHTML = `0 stories on <strong>‘${searchEscapeHtml(state.q)}’</strong> explore these instead`;
            } else {
                const count = bySort.length;
                emptyLine.hidden = false;
                emptyLine.textContent = count > SEARCH_RESULT_CAP
                    ? `${SEARCH_RESULT_CAP}+ results from TAOHS`
                    : `${count} ${count === 1 ? 'result' : 'results'} from TAOHS`;
            }
        }

        suggestStack.hidden = true;
        suggestStack.replaceChildren();

        if (!hasQuery) {
            searchFillRow(suggestRow, [], '');
            results.replaceChildren();
            results.hidden = true;
            return;
        }

        if (hasHits) {
            searchFillRow(suggestRow, bySort.slice(0, 3).map((entry) => entry.item), state.q);
            searchFill(results, bySort.map((entry) => entry.item).slice(0, SEARCH_RESULT_CAP), state.q);
            results.hidden = bySort.length === 0;
            return;
        }

        searchFillRow(suggestRow, [], '');
        searchFill(results, recentFive, state.q);
        results.hidden = recentFive.length === 0;
    };

    const runQuery = (replace) => {
        const state = readState();
        state.q = (input?.value || '').trim();
        writeState(state, replace);
        render();
    };

    form?.addEventListener('submit', (event) => {
        event.preventDefault();
        runQuery(false);
    });

    let typing;
    input?.addEventListener('input', () => {
        window.clearTimeout(typing);
        typing = window.setTimeout(() => runQuery(true), 180);
    });
    input?.addEventListener('search', () => runQuery(true));

    pills.forEach((link) => {
        link.addEventListener('click', (event) => {
            event.preventDefault();
            const state = readState();
            state.cat = link.getAttribute('data-cat') || '';
            writeState(state, false);
            render();
        });
    });

    root.querySelectorAll('[data-sort]').forEach((button) => {
        button.addEventListener('click', () => {
            const state = readState();
            state.sort = button.getAttribute('data-sort') || 'newest';
            writeState(state, false);
            closeDrops();
            render();
        });
    });

    articleFilter?.addEventListener('change', render);
    videoFilter?.addEventListener('change', render);

    root.querySelector('.js-date-prev')?.addEventListener('click', () => {
        cal.view = new Date(cal.view.getFullYear(), cal.view.getMonth() - 1, 1);
        renderCalendar();
    });
    root.querySelector('.js-date-next')?.addEventListener('click', () => {
        cal.view = new Date(cal.view.getFullYear(), cal.view.getMonth() + 1, 1);
        renderCalendar();
    });
    root.querySelector('.js-date-cancel')?.addEventListener('click', () => {
        cal.start = null;
        cal.end = null;
        if (dateBtnLabel) {
            dateBtnLabel.textContent = 'Dates';
        }
        closeDrops();
        renderCalendar();
        render();
    });
    root.querySelector('.js-date-apply')?.addEventListener('click', () => {
        if (dateBtnLabel) {
            dateBtnLabel.textContent = formatDateRangeLabel(cal.start, cal.end);
        }
        closeDrops();
        render();
    });
    clearBtn?.addEventListener('click', () => {
        cal.start = null;
        cal.end = null;
        if (articleFilter) {
            articleFilter.checked = false;
        }
        if (videoFilter) {
            videoFilter.checked = false;
        }
        if (dateBtnLabel) {
            dateBtnLabel.textContent = 'Dates';
        }
        if (filterLabel) {
            filterLabel.textContent = 'Filters';
        }
        if (input) {
            input.value = '';
        }
        const state = readState();
        state.sort = 'relevance';
        state.cat = '';
        state.q = '';
        writeState(state, false);
        closeDrops();
        renderCalendar();
        render();
    });

    const ingestMotion = (videos, base) => {
        if (!source) {
            return;
        }
        videos.forEach((video) => {
            const card = motionSearchItem(video, base);
            source.appendChild(card);
            items.push(card);
        });
        hideEmptyEditorialPills(items, pills);
        render();
    };

    window.addEventListener('popstate', render);
    hideEmptyEditorialPills(items, pills);
    renderCalendar();
    render();

    const directusUrl = root.getAttribute('data-directus-url') || '';
    const directusToken = root.getAttribute('data-directus-token') || '';
    if (directusUrl && directusToken) {
        fetchMotionVideos(directusUrl, directusToken)
            .then(({ payload, url }) => {
                const videos = (Array.isArray(payload?.data) ? payload.data : []).map(motionFromDirectus);
                ingestMotion(videos, url);
            })
            .catch(() => {});
    }
}

const MOTION_TAG = 'category_taohs-in-motion_scene-and-society';
const MOTION_FIELDS = [
    'id',
    'Title',
    'Slug',
    'YouTube_URL',
    'Excerpt',
    'Featured',
    'Published_at',
    'Cover.id',
    'Tags.Tags_id.Slug',
    'Tags.Tags_id.Name'
].join(',');

function motionFromDirectus(row) {
    const tags = Array.isArray(row?.Tags)
        ? row.Tags.map((item) => {
            const tag = item?.Tags_id || item;
            if (!tag || typeof tag !== 'object') {
                return null;
            }
            return {
                slug: tag.Slug || '',
                name: tag.Name || ''
            };
        }).filter(Boolean)
        : [];
    const cover = row?.Cover;

    return {
        id: row?.id,
        title: row?.Title || '',
        slug: row?.Slug || '',
        youtube_url: row?.YouTube_URL || '',
        excerpt: row?.Excerpt || '',
        featured: row?.Featured === true || row?.Featured === 1,
        published_at: row?.Published_at || '',
        cover: cover && (cover.id || typeof cover === 'string') ? cover : null,
        tags
    };
}

function motionYoutubeId(url) {
    if (!url) {
        return '';
    }
    const match = String(url).match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{11})/);
    return match ? match[1] : '';
}

function motionTags(video) {
    const raw = video?.tags;
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw.map((row) => row.tags_id || row.tags || row).filter(Boolean);
}

function motionHasCat(video, slug) {
    return motionTags(video).some((tag) => (tag.slug || '') === slug);
}

function motionLabel(video) {
    const tag = motionTags(video)[0];
    if (!tag?.name) {
        return '';
    }
    return leafLabel(tag.name);
}

function motionSearchItem(video, base) {
    const article = document.createElement('article');
    article.className = 'taohs-search-card js-search-item';
    const tags = motionTags(video);
    const slugs = tags.map((tag) => tag.slug).filter(Boolean);
    if (!slugs.length) {
        slugs.push('category_taohs-in-motion');
    }
    const names = tags.map((tag) => tag.name).filter(Boolean);
    const date = String(video.published_at || '').slice(0, 10);
    const cover = motionCover(base, video);
    const href = video.youtube_url || '/taohs-in-motion/';
    const cat = names[0] ? leafLabel(names[0]) : '';
    const title = video.title || '';
    article.setAttribute('data-title', title);
    article.setAttribute('data-excerpt', video.excerpt || '');
    article.setAttribute('data-tags', slugs.join(' '));
    article.setAttribute('data-tag-names', names.join(' '));
    article.setAttribute('data-date', date);
    article.setAttribute('data-featured', video.featured ? '1' : '0');
    article.setAttribute('data-url', href);
    const youtubeAttr = video.youtube_url ? ` data-youtube="${searchEscapeHtml(video.youtube_url)}"` : '';
    article.innerHTML = `<a href="${searchEscapeHtml(href)}"${youtubeAttr}>
        <div class="taohs-search-card-media">${cover ? `<img src="${searchEscapeHtml(cover)}" alt="${searchEscapeHtml(title)}" width="350" height="286" loading="lazy">` : ''}</div>
        <div class="taohs-search-card-body">
            ${cat ? `<span class="taohs-search-card-cat js-search-text">${searchEscapeHtml(cat)}</span>` : ''}
            <h3 class="taohs-search-card-title js-search-text">${searchEscapeHtml(title)}</h3>
            <p class="taohs-search-card-meta"><time datetime="${searchEscapeHtml(date)}">${searchEscapeHtml(date.slice(0, 4))}</time></p>
        </div>
    </a>`;
    return article;
}

function motionCover(base, video) {
    const file = video?.cover;
    const id = file && (file.id || (typeof file === 'string' ? file : ''));
    if (id && base) {
        return `${base}/assets/${id}`;
    }
    const youtube = motionYoutubeId(video?.youtube_url);
    return youtube ? `https://i.ytimg.com/vi/${youtube}/hqdefault.jpg` : '';
}

function motionCard(video, base) {
    const article = document.createElement('article');
    article.className = 'taohs-card taohs-card--motion';
    const href = video.youtube_url || '#';
    const cover = motionCover(base, video);
    const title = searchEscapeHtml(video.title || '');
    const youtubeAttr = video.youtube_url ? ` data-youtube="${searchEscapeHtml(video.youtube_url)}"` : '';
    article.innerHTML = `<a href="${searchEscapeHtml(href)}"${youtubeAttr}>
        <div class="taohs-card-media">${cover ? `<img src="${searchEscapeHtml(cover)}" alt="${title}" width="384" height="320" loading="lazy">` : ''}</div>
        <div class="taohs-card-body">
            <h3 class="taohs-card-title">${title}</h3>
        </div>
    </a>`;
    return article;
}

let motionYoutubeApi;

function loadMotionYoutubeApi() {
    if (window.YT?.Player) {
        return Promise.resolve(window.YT);
    }
    if (motionYoutubeApi) {
        return motionYoutubeApi;
    }
    motionYoutubeApi = new Promise((resolve) => {
        const previous = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (typeof previous === 'function') {
                previous();
            }
            resolve(window.YT);
        };
        if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
            const script = document.createElement('script');
            script.src = 'https://www.youtube.com/iframe_api';
            document.head.appendChild(script);
        }
    });
    return motionYoutubeApi;
}

function muteMotionCaptions(player) {
    try {
        player.unloadModule('captions');
        player.unloadModule('cc');
    } catch (err) {
        // YouTube may throw if the module is already gone.
    }
}

function fetchMotionVideos(directusUrl, directusToken) {
    const url = String(directusUrl || '').replace(/\/$/, '');
    const token = String(directusToken || '');
    if (!url || !token) {
        return Promise.reject(new Error('directus config'));
    }
    const params = new URLSearchParams({
        fields: MOTION_FIELDS,
        'filter[Status][_eq]': 'published',
        sort: '-Published_at',
        limit: '-1'
    });
    return fetch(`${url}/items/Videos?${params}`, {
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
        }
    }).then((res) => {
        if (!res.ok) {
            throw new Error(`videos ${res.status}`);
        }
        return res.json().then((payload) => ({ payload, url }));
    });
}

function motionFeatureSlide(video, base, playSrc) {
    const slide = document.createElement('article');
    slide.className = 'taohs-motion-slide';
    const cover = motionCover(base, video);
    const youtube = motionYoutubeId(video.youtube_url);
    const label = searchEscapeHtml(motionLabel(video));
    const title = searchEscapeHtml(video.title || '');
    slide.innerHTML = `<div class="taohs-motion-media">
        ${cover ? `<img src="${searchEscapeHtml(cover)}" alt="${title}" width="1184" height="670">` : ''}
        <div class="taohs-motion-copy">
            ${label ? `<span class="taohs-cat">${label}</span>` : ''}
            <h2>${title}</h2>
            <button class="taohs-btn taohs-btn--white js-motion-play" type="button">
                <span class="taohs-icon taohs-icon--sm"><img src="${searchEscapeHtml(playSrc)}" alt="" width="20" height="20"></span>
                Play
            </button>
        </div>
    </div>`;
    slide.querySelector('.js-motion-play')?.addEventListener('click', () => {
        const media = slide.querySelector('.taohs-motion-media');
        if (!youtube || !media || media.querySelector('iframe')) {
            return;
        }
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube.com/embed/${youtube}?autoplay=1&controls=1&rel=0&playsinline=1&origin=${encodeURIComponent(window.location.origin)}`;
        iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen');
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('title', video.title || 'Video');
        media.appendChild(iframe);
        slide.classList.add('is-playing');
    });
    return slide;
}

export function motionPage() {
    const root = document.querySelector('[data-motion]');
    if (!root) {
        return;
    }

    const directusUrl = root.getAttribute('data-directus-url') || '';
    const directusToken = root.getAttribute('data-directus-token') || '';
    const playSrc = root.getAttribute('data-icon-play') || '';
    const featuredTrack = root.querySelector('.js-motion-featured');
    const railTrack = root.querySelector('.js-motion-rail');
    const grid = root.querySelector('.js-motion-grid');
    const feature = root.querySelector('[data-panel="feature"]');
    const all = root.querySelector('[data-panel="all"]');
    const list = root.querySelector('[data-panel="list"]');
    const pills = [...root.querySelectorAll('.taohs-pills a')];
    let featureCount = -1;

    const apply = () => {
        const hash = (window.location.hash || '').replace('#', '');
        const isAll = hash === 'view-all';
        const isCat = hash === 'scene-and-society';
        if (feature) {
            feature.hidden = isCat || featureCount === 0;
        }
        if (all) {
            all.hidden = !isAll;
        }
        if (list) {
            list.hidden = !isCat;
        }
        pills.forEach((link) => {
            const active =
                (isAll && link.getAttribute('data-view') === 'all') ||
                (isCat && link.getAttribute('data-cat') === hash);
            link.classList.toggle('is-active', active);
        });
    };

    window.addEventListener('hashchange', apply);
    apply();

    if (!directusUrl || !directusToken || !featuredTrack) {
        featureCount = 0;
        apply();
        return;
    }

    fetchMotionVideos(directusUrl, directusToken)
        .then(({ payload, url }) => {
            const videos = (Array.isArray(payload?.data) ? payload.data : []).map(motionFromDirectus);
            const tagged = videos.filter((video) => motionHasCat(video, MOTION_TAG));
            const scene = tagged.length ? tagged : videos;
            const featured = videos.filter((video) => video.featured);
            const featureList = featured.length ? featured : videos;
            featureCount = featureList.length;

            featuredTrack.replaceChildren(...featureList.map((video) => motionFeatureSlide(video, url, playSrc)));
            feature?.classList.toggle('is-single', featureList.length < 2);

            if (railTrack) {
                railTrack.replaceChildren(...scene.map((video) => motionCard(video, url)));
            }
            if (grid) {
                grid.replaceChildren(...scene.map((video) => motionCard(video, url)));
            }
            apply();
        })
        .catch(() => {
            featureCount = 0;
            apply();
        });
}

export function syncMotionNav() {
    const nodes = document.querySelectorAll('.js-motion-nav');
    if (!nodes.length) {
        return;
    }

    const directusUrl = document.body.getAttribute('data-directus-url') || '';
    const directusToken = document.body.getAttribute('data-directus-token') || '';
    const hide = () => {
        nodes.forEach((node) => {
            node.hidden = true;
        });
    };

    if (!directusUrl || !directusToken) {
        hide();
        return;
    }

    fetchMotionVideos(directusUrl, directusToken)
        .then(({ payload }) => {
            const videos = Array.isArray(payload?.data) ? payload.data : [];
            nodes.forEach((node) => {
                node.hidden = videos.length === 0;
            });
        })
        .catch(hide);
}

export function homeMotion() {
    const root = document.querySelector('[data-home-motion]');
    if (!root) {
        return;
    }

    const directusUrl = root.getAttribute('data-directus-url') || '';
    const directusToken = root.getAttribute('data-directus-token') || '';
    const playSrc = root.getAttribute('data-icon-play') || '';
    const mount = root.querySelector('.js-home-motion-feature');
    const hide = () => {
        root.hidden = true;
    };

    if (!directusUrl || !directusToken || !mount) {
        hide();
        return;
    }

    fetchMotionVideos(directusUrl, directusToken)
        .then(({ payload, url }) => {
            const videos = (Array.isArray(payload?.data) ? payload.data : []).map(motionFromDirectus);
            const featured = videos.find((video) => video.featured);
            if (!featured) {
                hide();
                return;
            }
            mount.replaceChildren(motionFeatureSlide(featured, url, playSrc));
            root.hidden = false;
        })
        .catch(hide);
}

export function bindMotionVideoModal() {
    const modal = document.getElementById('taohs-video-modal');
    if (!modal) {
        return;
    }
    const frame = modal.querySelector('.js-video-frame');
    const youtubeLink = modal.querySelector('.js-video-youtube');
    const closeBtn = modal.querySelector('.js-video-close');

    const close = () => {
        modal.hidden = true;
        modal.classList.remove('is-open');
        document.body.style.overflow = '';
        if (frame) {
            frame.replaceChildren();
        }
        if (youtubeLink) {
            youtubeLink.href = '#';
        }
    };

    const open = (youtubeUrl) => {
        const id = motionYoutubeId(youtubeUrl);
        if (!id || !frame) {
            return;
        }
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube.com/embed/${id}?autoplay=1&controls=1&rel=0&playsinline=1&origin=${encodeURIComponent(window.location.origin)}`;
        iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen');
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('title', 'Video');
        frame.replaceChildren(iframe);
        if (youtubeLink) {
            youtubeLink.href = youtubeUrl;
        }
        modal.hidden = false;
        modal.classList.add('is-open');
        document.body.style.overflow = 'hidden';
    };

    document.addEventListener('click', (event) => {
        if (event.target.closest('.js-video-youtube') || event.target.closest('.js-video-close')) {
            return;
        }
        const link = event.target.closest('[data-youtube]');
        if (!link) {
            return;
        }
        const youtubeUrl = link.getAttribute('data-youtube') || '';
        if (!motionYoutubeId(youtubeUrl)) {
            return;
        }
        event.preventDefault();
        open(youtubeUrl);
    });
    closeBtn?.addEventListener('click', close);
    modal.addEventListener('click', (event) => {
        if (event.target === modal) {
            close();
        }
    });
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && modal.classList.contains('is-open')) {
            close();
        }
    });
}

